import { WithLoader } from '@/components/WithLoader';
import { statisticsQueryOptions } from '@/dashboards/TitleDashboard/api';
import { useDuckDBQuery } from '@/duckdb';
import {
    Can,
    FEATURE_FLAG_ACTIONS,
    FEATURE_FLAG_SUBJECTS,
} from '@/features/Casl';
import { useAbilityContext } from '@/features/Casl/hooks';

import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

import { ADJ_R2_GRADIENT, DURBIN_WATSON_GRADIENT } from './constants';
import { ModelAccuracyChart } from './ModelAccuracyChart';
import { PredictionErrorChart } from './PredictionErrorChart';
import { StatRow } from './StatRow';

export const ModelStatisticsCard = () => {
    const {
        data: statisticsData,
        isLoading,
        isFetching,
    } = useDuckDBQuery(statisticsQueryOptions());

    const {
        dashboard: { configs },
    } = useDashboardGlobalContext();

    const displayAsError =
        configs.find((config) => config.key === 'MODEL_ACURACY_AS_ERROR')
            ?.value === JSON.stringify(true);

    const { ability } = useAbilityContext();

    const canViewWeekly = ability.can(
        FEATURE_FLAG_ACTIONS.VIEW,
        FEATURE_FLAG_SUBJECTS.TITLE_DASHBOARD_WEEKLY_ACCURACY,
    );
    const canViewMonthly = ability.can(
        FEATURE_FLAG_ACTIONS.VIEW,
        FEATURE_FLAG_SUBJECTS.TITLE_DASHBOARD_MONTHLY_ACCURACY,
    );

    return (
        <div className="flex flex-col">
            <div className="glass-tile rounded-xl font-thin flex flex-col w-full px-5 py-3 text-base leading-6">
                <WithLoader
                    isLoading={isLoading}
                    isFetching={isFetching}
                    loadingMessage={false}
                    delayedMessage={false}
                >
                    <div className="flex flex-col gap-3">
                        <Can
                            I={FEATURE_FLAG_ACTIONS.VIEW}
                            a={
                                FEATURE_FLAG_SUBJECTS.TITLE_DASHBOARD_ADJUSTED_R2
                            }
                        >
                            <StatRow
                                label="Adj R²"
                                tooltip="What portion of the KPI was explained by the
                                model. Higher is better."
                                value={statisticsData?.adjustedR2}
                                min={0}
                                max={100}
                                gradient={ADJ_R2_GRADIENT}
                                formatValue={(v) => `${v.toFixed(2)}%`}
                            />
                        </Can>
                        <Can
                            I={FEATURE_FLAG_ACTIONS.VIEW}
                            a={
                                FEATURE_FLAG_SUBJECTS.TITLE_DASHBOARD_DURBIN_WATSON_INDEX
                            }
                        >
                            <StatRow
                                label="Durbin-Watson"
                                tooltip="A statistical check that all the model trends/media adstock are correctly accounted for. A value between 1.4-2.2 is considered good."
                                value={statisticsData?.durbinWatson}
                                min={0}
                                max={4}
                                gradient={DURBIN_WATSON_GRADIENT}
                                formatValue={(v) => v.toFixed(2)}
                            />
                        </Can>
                        {displayAsError ? (
                            <PredictionErrorChart
                                weeklyAccuracy={
                                    canViewWeekly
                                        ? statisticsData?.weeklyAccuracy
                                        : null
                                }
                                monthlyAccuracy={
                                    canViewMonthly
                                        ? statisticsData?.monthlyAccuracy
                                        : null
                                }
                            />
                        ) : (
                            <ModelAccuracyChart
                                weeklyAccuracy={
                                    canViewWeekly
                                        ? statisticsData?.weeklyAccuracy
                                        : null
                                }
                                monthlyAccuracy={
                                    canViewMonthly
                                        ? statisticsData?.monthlyAccuracy
                                        : null
                                }
                            />
                        )}
                    </div>
                </WithLoader>
            </div>
        </div>
    );
};
